import { HudFrame } from './HudFrame';
import {
  getArmorProfile,
  getWeaponIdentity,
  legalWeaponIdsForArmor,
  WEAPON_IDENTITIES,
  type ArmorClass,
  type WeaponId,
} from '../shared/game';
import { WEAPONS } from '../shared/constants';
import { titleCase as sharedTitleCase } from '../shared/stringUtils';
import { resolveEnergyBarColor, resolveHealthBarColor } from './hudDesign';

// Phase 115 — Ammo readout per weapon. Energy/hitscan = ∞, projectile = X/Y.
function ammoReadoutForWeapon(weaponId: WeaponId): string {
  const def = WEAPONS[weaponId];
  if (!def) return '∞';
  if (def.fireMode === 'hitscan') return '∞';
  // Treat blaster as energy-based fallback with ∞ ammo, projectiles tracked X/Y for others
  if (weaponId === 'blaster') return '∞';
  // Default magazine sizes per role
  const max = def.combatRole === 'heavy' ? 10 : def.combatRole === 'burst' ? 16 : 20;
  return `${max}/${max}`;
}

type HudPlayerSnapshot = {
  health?: number;
  energy?: number;
  speed?: number;
  armorClass?: string | null;
  selectedWeapon?: string | null;
  weaponHeat?: number;
  weaponCooldown?: number;
  overheated?: boolean;
  spawnProtected?: boolean;
  carryingFlagTeam?: string | null;
  packSlot?: number | null;
  packId?: string | null;
  packLabel?: string | null;
  packType?: string | null;
  packState?: string | null;
  packActive?: boolean;
  packReady?: boolean;
  packCooldownSeconds?: number;
  packCharges?: number | null;
  packMaxCharges?: number | null;
  packEnergy?: number | null;
  packOptions?: Array<{
    slot?: number | null;
    id?: string | null;
    label?: string | null;
    type?: string | null;
    state?: string | null;
    active?: boolean;
    ready?: boolean;
    cooldownSeconds?: number;
    charges?: number | null;
    maxCharges?: number | null;
    energy?: number | null;
  }>;
  grenadeSlot?: number | null;
  grenadeId?: string | null;
  grenadeLabel?: string | null;
  grenadeType?: string | null;
  grenadeState?: string | null;
  grenadeActive?: boolean | null;
  grenadeReady?: boolean | null;
  grenadeCooldownSeconds?: number | null;
  grenadeCharges?: number | null;
  grenadeMaxCharges?: number | null;
  grenadeOptions?: Array<{
    slot?: number | null;
    id?: string | null;
    label?: string | null;
    type?: string | null;
    state?: string | null;
    active?: boolean | null;
    ready?: boolean | null;
    cooldownSeconds?: number | null;
    charges?: number | null;
    maxCharges?: number | null;
  }>;
};

type WeaponPanelProps = {
  localPlayer: HudPlayerSnapshot;
  compact?: boolean;
  minimal?: boolean;
  stacked?: boolean;
};

type WeaponRole = 'burst' | 'sustained' | 'siege' | 'sidearm' | 'grenade' | 'utility' | 'snipe' | 'melee' | 'generic';

type WeaponProfile = {
  rawId: string | null;
  label: string;
  slotLabel: string;
  roleLabel: string;
  tone: string;
  role: WeaponRole;
  detail: string;
  cooldownWindow: number;
};

type PackProfile = {
  slotLabel: string;
  label: string;
  tone: string;
  status: string;
  detail: string;
  active: boolean;
  ready: boolean;
  cooldownSeconds: number;
  options: NonNullable<HudPlayerSnapshot['packOptions']>;
};

type GrenadeProfile = {
  slotLabel: string;
  label: string;
  tone: string;
  status: string;
  detail: string;
  active: boolean;
  ready: boolean;
  cooldownSeconds: number;
  options: NonNullable<HudPlayerSnapshot['grenadeOptions']>;
};

type WeaponTelemetryCue = {
  action: string;
  detail: string;
  tone: string;
  readiness: number;
  cycleText: string;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function frameClip() {
  return 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))';
}

function titleCase(value: string) {
  return sharedTitleCase(value);
}

function packTone(value: HudPlayerSnapshot) {
  const key = `${value.packLabel ?? value.packType ?? value.packId ?? ''}`.toLowerCase();

  if (/speed|boost|dash|rush/.test(key)) {
    return '#62ddff';
  }

  if (/shield|guard|barrier|bulk/.test(key)) {
    return '#86a9ff';
  }

  if (/energy|battery|capacitor|power/.test(key)) {
    return '#b58cff';
  }

  if (/repair|utility|sensor|vision|ammo/.test(key)) {
    return '#ffcc66';
  }

  return '#9fb4c7';
}

function classifyPack(pack: HudPlayerSnapshot): PackProfile {
  const slot = pack.packSlot ?? null;
  const label = pack.packLabel ?? pack.packType ?? pack.packId ?? (slot ? `Pack ${slot}` : 'Pack');
  const rawCooldown = Math.max(0, pack.packCooldownSeconds ?? 0);
  const cooldownSeconds = rawCooldown < 0.08 ? 0 : rawCooldown;
  const active = Boolean(pack.packActive);
  const ready = Boolean(pack.packReady ?? (!active && cooldownSeconds <= 0));
  const status = pack.packState && pack.packState.length ? pack.packState.toUpperCase() : active ? 'ACTIVE' : ready ? 'READY' : `COOLDOWN ${cooldownSeconds.toFixed(1)}S`;
  const detail =
    pack.packCharges != null && pack.packMaxCharges != null
      ? `${pack.packCharges}/${pack.packMaxCharges} charges`
      : pack.packEnergy != null
        ? `${Math.round(pack.packEnergy)} energy`
        : 'Q/E cycle · 5-8 direct · R activate';

  return {
    slotLabel: slot ? `Slot ${slot}` : 'Pack',
    label: titleCase(label),
    tone: packTone(pack),
    status,
    detail,
    active,
    ready,
    cooldownSeconds,
    options: pack.packOptions ?? [],
  };
}

function grenadeTone(value: HudPlayerSnapshot) {
  const key = `${value.grenadeLabel ?? value.grenadeType ?? value.grenadeId ?? ''}`.toLowerCase();

  if (/concussion|shock|stun|blast/.test(key)) {
    return '#86a9ff';
  }

  if (/frag|shrapnel|fragment|explosive/.test(key)) {
    return '#ff9866';
  }

  if (/utility|smoke|flash|disrupt/.test(key)) {
    return '#b58cff';
  }

  return '#9fb4c7';
}

function classifyGrenade(grenade: HudPlayerSnapshot): GrenadeProfile {
  const slot = grenade.grenadeSlot ?? null;
  const label = grenade.grenadeLabel ?? grenade.grenadeType ?? grenade.grenadeId ?? (slot ? `Grenade ${slot}` : 'Grenade');
  const rawCooldown = Math.max(0, grenade.grenadeCooldownSeconds ?? 0);
  const cooldownSeconds = rawCooldown < 0.08 ? 0 : rawCooldown;
  const active = Boolean(grenade.grenadeActive);
  const ready = Boolean(grenade.grenadeReady ?? (!active && cooldownSeconds <= 0));
  const status =
    grenade.grenadeState && grenade.grenadeState.length ? grenade.grenadeState.toUpperCase() : active ? 'ACTIVE' : ready ? 'READY' : `COOLDOWN ${cooldownSeconds.toFixed(1)}S`;
  const detail =
    grenade.grenadeCharges != null && grenade.grenadeMaxCharges != null
      ? `${grenade.grenadeCharges}/${grenade.grenadeMaxCharges} charges · F throw · H cycle`
      : grenade.grenadeType
        ? `${titleCase(grenade.grenadeType)} · F throw · H cycle`
        : 'F throw · H cycle';

  return {
    slotLabel: slot ? `Slot ${slot}` : 'Grenade',
    label: titleCase(label),
    tone: grenadeTone(grenade),
    status,
    detail,
    active,
    ready,
    cooldownSeconds,
    options: grenade.grenadeOptions ?? [],
  };
}

const LOADOUT_SLOTS = [
  { slot: 1, label: WEAPON_IDENTITIES.spinfusor.label.toUpperCase(), weaponId: 'spinfusor' },
  { slot: 2, label: WEAPON_IDENTITIES.chaingun.label.toUpperCase(), weaponId: 'chaingun' },
  { slot: 3, label: WEAPON_IDENTITIES.mortar.label.toUpperCase(), weaponId: 'mortar' },
  { slot: 4, label: WEAPON_IDENTITIES.blaster.label.toUpperCase(), weaponId: 'blaster' },
] as const;

function normalizeArmorClass(value?: string | null): ArmorClass {
  return value === 'light' || value === 'heavy' || value === 'medium' ? value : 'medium';
}

function loadoutRestrictionLabel(armorClass: ArmorClass, weaponId: WeaponId) {
  if (legalWeaponIdsForArmor(armorClass).includes(weaponId)) {
    return null;
  }

  return weaponId === 'mortar' ? 'HEAVY ONLY' : 'LOCKED';
}

function classifyWeapon(weaponId?: string | null): WeaponProfile {
  const rawId = weaponId && weaponId.length ? weaponId : null;
  const key = (rawId ?? '').toLowerCase();
  const identity = getWeaponIdentity(key);

  if (identity) {
    return {
      rawId,
      label: identity.label,
      slotLabel: identity.slotLabel,
      roleLabel: identity.roleLabel,
      tone: identity.tone,
      role: identity.role,
      detail: identity.detail,
      cooldownWindow: identity.cooldownWindow,
    };
  }

  const isBurst = /spinfusor|disc|burst|plasma|launcher|grenade/.test(key);
  const isSustained = /chaingun|chain|gun|bullet|slug|rifle|minigun|auto/.test(key);
  const isUtility = /repair|heal|support|boost|sensor|jam|tool/.test(key);

  let role: WeaponRole = 'generic';
  let tone = '#9fb4c7';
  let roleLabel = 'Generic';
  let slotLabel = 'Loadout';
  let detail = rawId ? `${titleCase(rawId)} is active in the current loadout.` : 'No active weapon selected.';
  let cooldownWindow = 1;

  if (isBurst) {
    role = 'burst';
    tone = '#62ddff';
    roleLabel = 'Burst';
    slotLabel = 'Slot 1';
    detail = 'High-impact splash weapon for route control.';
  } else if (isSustained) {
    role = 'sustained';
    tone = '#ffcc66';
    roleLabel = 'Sustained';
    slotLabel = 'Slot 2';
    detail = 'Continuous pressure weapon for tracking damage.';
  } else if (isUtility) {
    role = 'utility';
    tone = '#b58cff';
    roleLabel = 'Utility';
    slotLabel = 'Utility';
    detail = 'Support-oriented tool with situational uptime.';
  } else if (rawId) {
    const hash = rawId
      .split('')
      .reduce((accumulator, character) => ((accumulator * 33 + character.charCodeAt(0)) >>> 0), 0);
    const palette = ['#62ddff', '#ffcc66', '#b58cff'];
    tone = palette[hash % palette.length];
    roleLabel = 'Generic';
  }

  return {
    rawId,
    label: rawId ? titleCase(rawId) : 'Unarmed',
    slotLabel,
    roleLabel,
    tone,
    role,
    detail,
    cooldownWindow,
  };
}

function MetricCard({
  label,
  value,
  tone,
  detail,
  compact = false,
}: {
  label: string;
  value: string;
  tone: string;
  detail?: string;
  compact?: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 6,
        minWidth: 0,
        padding: compact ? '10px 11px' : '11px 12px',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        clipPath: frameClip(),
        WebkitClipPath: frameClip(),
        boxShadow: `inset 0 0 0 1px ${tone}12`,
      }}
    >
      <span style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: tone, lineHeight: 1.1 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <strong style={{ fontSize: compact ? 24 : 26, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: '#f4fbff' }}>{value}</strong>
        <span style={{ fontSize: 10, letterSpacing: 1.6, textTransform: 'uppercase', color: 'rgba(231,244,252,0.48)' }}>HUD</span>
      </div>
      {detail ? (
        <div
          style={{
            fontSize: 10.5,
            lineHeight: 1.25,
            color: 'rgba(231,244,252,0.58)',
            letterSpacing: 0.4,
          }}
        >
          {detail}
        </div>
      ) : null}
    </div>
  );
}

function Meter({
  label,
  value,
  max = 100,
  tone,
  activeLabel,
}: {
  label: string;
  value: number;
  max?: number;
  tone: string;
  activeLabel?: string;
}) {
  const pct = clamp((value / max) * 100, 0, 100);

  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11 }}>
        <span style={{ color: 'rgba(231,244,252,0.68)', letterSpacing: 1.6, textTransform: 'uppercase' }}>{label}</span>
        <span style={{ color: '#f4fbff', fontVariantNumeric: 'tabular-nums' }}>{activeLabel ?? `${Math.round(value)}%`}</span>
      </div>
      <div
        style={{
          height: 8,
          background: 'rgba(255,255,255,0.06)',
          overflow: 'hidden',
          clipPath: frameClip(),
          WebkitClipPath: frameClip(),
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${tone}, rgba(255,255,255,0.84))`,
            boxShadow: `0 0 18px ${tone}40`,
            transition: 'width 0.3s ease-out',
          }}
        />
      </div>
    </div>
  );
}

function summarizeWeaponTelemetry(input: {
  weapon: WeaponProfile;
  cooldown: number;
  heat: number;
  overheated: boolean;
  spawnProtected: boolean;
  carryingFlag: string | null;
  speed: number;
}): WeaponTelemetryCue {
  const cooldownRatio = clamp(input.cooldown / Math.max(0.12, input.weapon.cooldownWindow), 0, 1);
  const heatRatio = clamp(input.heat / 100, 0, 1);
  const readinessPenalty = cooldownRatio * 60 + heatRatio * 45 + (input.overheated ? 25 : 0) + (input.spawnProtected ? 30 : 0);
  const readiness = Math.round(clamp(100 - readinessPenalty, 0, 100));
  const cycleText = input.cooldown > 0.02 ? `Cycle ${input.cooldown.toFixed(2)}s` : 'Cycle Open';

  if (input.spawnProtected) {
    return {
      action: 'Hold fire',
      detail: 'Spawn protection active. Reposition and build angle before commit.',
      tone: '#62ddff',
      readiness,
      cycleText,
    };
  }

  if (input.overheated) {
    return {
      action: 'Cool down',
      detail: 'Weapon locked. Feather movement and break line of sight.',
      tone: '#ff8d74',
      readiness,
      cycleText,
    };
  }

  if (input.cooldown > 0.04) {
    return {
      action: 'Stagger shots',
      detail: 'Weapon cycle in progress. Keep momentum and avoid over-commit.',
      tone: '#ffcc66',
      readiness,
      cycleText,
    };
  }

  if (input.carryingFlag) {
    return {
      action: 'Escort route',
      detail: 'Prioritize survival lanes and deny close chasers.',
      tone: '#ffcc66',
      readiness,
      cycleText,
    };
  }

  if (input.weapon.role === 'siege') {
    return {
      action: 'Lead arc',
      detail: input.speed > 26 ? 'Fire ahead of fast movers and punish base doors.' : 'Use splash to clear anchor points.',
      tone: input.weapon.tone,
      readiness,
      cycleText,
    };
  }

  if (input.weapon.role === 'burst') {
    return {
      action: 'Burst entry',
      detail: input.speed > 30 ? 'Shoot into lane crossings and route choke exits.' : 'Play mid-range splash and punish overexposure.',
      tone: input.weapon.tone,
      readiness,
      cycleText,
    };
  }

  if (input.weapon.role === 'sustained') {
    return {
      action: 'Track pressure',
      detail: 'Maintain beam time on exposed targets and finish low-health runners.',
      tone: input.weapon.tone,
      readiness,
      cycleText,
    };
  }

  return {
    action: 'Commit duel',
    detail: 'Weapon window is open. Take favorable fights with momentum.',
    tone: input.weapon.tone,
    readiness,
    cycleText,
  };
}

export function WeaponPanel({ localPlayer, compact = false, minimal = false, stacked = false }: WeaponPanelProps) {
  const armorClass = normalizeArmorClass(localPlayer.armorClass);
  const armorProfile = getArmorProfile(armorClass);
  const legalWeapons = new Set(legalWeaponIdsForArmor(armorClass));
  const weapon = classifyWeapon(localPlayer.selectedWeapon ?? null);
  const health = clamp(localPlayer.health ?? 0, 0, armorProfile.maxHealth);
  const energy = clamp(localPlayer.energy ?? 0, 0, armorProfile.maxEnergy);
  const speed = Math.max(0, localPlayer.speed ?? 0);
  const heat = clamp(localPlayer.weaponHeat ?? 0, 0, 100);
  const cooldown = clamp(localPlayer.weaponCooldown ?? 0, 0, 100);
  const overheated = Boolean(localPlayer.overheated);
  const spawnProtected = Boolean(localPlayer.spawnProtected);
  const carryingFlag = localPlayer.carryingFlagTeam ?? null;
  const telemetry = summarizeWeaponTelemetry({
    weapon,
    cooldown,
    heat,
    overheated,
    spawnProtected,
    carryingFlag,
    speed,
  });
  const pack = classifyPack(localPlayer);
  const grenade = classifyGrenade(localPlayer);
  const packOptions = pack.options.length
      ? pack.options
      : [
        {
          slot: 1,
          label: 'Pack 1',
          state: pack.ready ? 'ready' : 'cooldown',
          active: Boolean(pack.active),
          ready: Boolean(pack.ready),
          cooldownSeconds: pack.cooldownSeconds ?? 0,
        },
      ];
  const grenadeOptions = grenade.options.length
      ? grenade.options
      : [
        {
          slot: 1,
          label: 'Grenade 1',
          state: grenade.ready ? 'ready' : 'cooldown',
          active: Boolean(grenade.active),
          ready: Boolean(grenade.ready),
          cooldownSeconds: grenade.cooldownSeconds ?? 0,
        },
      ];

  const heatLabel =
    weapon.role === 'sustained'
      ? overheated
        ? 'Overheat'
        : 'Weapon Heat'
      : weapon.role === 'burst'
        ? 'Disc Charge'
      : weapon.role === 'siege'
        ? 'Siege Load'
        : weapon.role === 'sidearm'
          ? 'Capacitor'
          : weapon.role === 'grenade'
            ? 'Fuse Window'
            : weapon.role === 'utility'
              ? 'System Load'
              : 'Weapon Load';

  return (
    <HudFrame
      title="LOADOUT"
      subtitle={
        carryingFlag
          ? `Carrying ${carryingFlag.toUpperCase()} flag`
          : `${armorClass.toUpperCase()} armor · ${weapon.label} online`
      }
      tone={overheated ? 'red' : 'cyan'}
      style={{
        position: compact || stacked ? 'relative' : 'absolute',
        left: compact || stacked ? undefined : 14,
        bottom: compact || stacked ? undefined : 14,
        width: compact || stacked ? '100%' : 'min(320px, calc(100vw - 28px))',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div style={{ display: 'grid', gap: minimal ? 9 : 12 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: minimal ? 'minmax(0, 1fr)' : compact ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
            gap: 8,
          }}
        >
          <div style={compact || minimal ? { gridColumn: '1 / -1' } : undefined}>
            <MetricCard
              label="Weapon"
              value={weapon.label}
              tone={weapon.tone}
              detail={minimal ? undefined : weapon.detail}
              compact={compact}
            />
          </div>
          {minimal ? null : (
            <>
              <MetricCard
                label="Slot"
                value={weapon.slotLabel}
                tone={weapon.tone}
                detail={weapon.rawId ? `Resolved from ${weapon.rawId}` : 'No slot selected'}
                compact={compact}
              />
              <MetricCard
                label="Role"
                value={weapon.roleLabel}
                tone={weapon.tone}
                detail={
                  weapon.role === 'burst'
                    ? 'Splash entry / chase punish'
                    : weapon.role === 'sustained'
                      ? 'Tracking pressure / cleanup'
                      : weapon.role === 'siege'
                        ? 'Base shelling / room denial'
                        : weapon.role === 'sidearm'
                          ? 'Interior duels / fast follow-up'
                          : weapon.role === 'grenade'
                            ? 'Stand clear / route denial'
                          : weapon.role === 'utility'
                            ? 'Support / situational system'
                            : 'Generic fallback'
                }
                compact={compact}
              />
            </>
          )}
        </div>

        {minimal ? null : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: compact ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
              gap: 8,
            }}
          >
            {LOADOUT_SLOTS.map((entry) => {
              const selected = entry.weaponId === weapon.rawId;
              const legal = legalWeapons.has(entry.weaponId);
              const restriction = loadoutRestrictionLabel(armorClass, entry.weaponId);
              return (
                <div
                  key={entry.weaponId}
                  style={{
                    display: 'grid',
                    gap: 4,
                    minWidth: 0,
                    padding: '8px 9px',
                    border: `1px solid ${selected ? `${weapon.tone}55` : legal ? 'rgba(255,255,255,0.08)' : 'rgba(255,141,116,0.18)'}`,
                    background: selected ? `${weapon.tone}14` : legal ? 'rgba(255,255,255,0.03)' : 'rgba(255,141,116,0.04)',
                    boxShadow: selected ? `inset 0 0 0 1px ${weapon.tone}1f` : 'none',
                    clipPath: frameClip(),
                    WebkitClipPath: frameClip(),
                    opacity: legal ? 1 : 0.58,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span style={{ fontSize: 10, letterSpacing: 1.8, textTransform: 'uppercase', color: selected ? weapon.tone : legal ? 'rgba(231,244,252,0.5)' : '#ff8d74' }}>
                    {restriction ? `${entry.slot} · ${restriction}` : entry.slot}
                  </span>
                  <strong
                    style={{
                      fontSize: 11,
                      lineHeight: 1.1,
                      letterSpacing: 0.8,
                      textTransform: 'uppercase',
                      color: selected ? '#f4fbff' : legal ? 'rgba(231,244,252,0.74)' : 'rgba(255,222,214,0.66)',
                    }}
                  >
                    {entry.label}
                  </strong>
                  <span
                    style={{
                      fontSize: 9.5,
                      letterSpacing: 1.2,
                      fontVariantNumeric: 'tabular-nums',
                      color: selected ? weapon.tone : 'rgba(231,244,252,0.55)',
                    }}
                  >
                    {ammoReadoutForWeapon(entry.weaponId as WeaponId)}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {minimal ? null : (
          <div
            style={{
              display: 'grid',
              gap: 5,
              padding: compact || minimal ? '8px 9px' : '9px 10px',
              background: `${telemetry.tone}10`,
              border: `1px solid ${telemetry.tone}34`,
              boxShadow: `inset 0 0 0 1px ${telemetry.tone}14`,
              clipPath: frameClip(),
              WebkitClipPath: frameClip(),
              color: '#edf7ff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, letterSpacing: 1.7, textTransform: 'uppercase', color: telemetry.tone }}>Telemetry</span>
              <span style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(231,244,252,0.66)' }}>
                Ready {telemetry.readiness}% · {telemetry.cycleText}
              </span>
            </div>
            <strong style={{ fontSize: compact || minimal ? 12 : 12.5, letterSpacing: 0.7, textTransform: 'uppercase' }}>{telemetry.action}</strong>
            <span style={{ fontSize: 10.5, lineHeight: 1.35, color: 'rgba(231,244,252,0.74)' }}>{telemetry.detail}</span>
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gap: 6,
            padding: compact || minimal ? '8px 9px' : '9px 10px',
            background: grenade.ready ? 'rgba(134,169,255,0.08)' : 'rgba(255,255,255,0.03)',
            border: `1px solid ${grenade.ready ? 'rgba(134,169,255,0.24)' : 'rgba(255,255,255,0.08)'}`,
            clipPath: frameClip(),
            WebkitClipPath: frameClip(),
            boxShadow: `inset 0 0 0 1px ${grenade.tone}10`,
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 10, letterSpacing: 1.8, textTransform: 'uppercase', color: grenade.tone }}>{grenade.slotLabel}</span>
            <strong style={{ fontSize: compact || minimal ? 13 : 14, letterSpacing: 0.8, textTransform: 'uppercase', color: '#f4fbff' }}>
              {grenade.label}
            </strong>
            <span style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'rgba(231,244,252,0.58)' }}>{grenade.status}</span>
          </div>
          {minimal ? null : <div style={{ fontSize: 10.5, lineHeight: 1.35, color: 'rgba(231,244,252,0.70)' }}>{grenade.detail}</div>}
          {minimal ? null : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {grenadeOptions.slice(0, 4).map((option, index) => {
                const optionSlot = option.slot ?? index + 1;
                const selected = optionSlot === (localPlayer.grenadeSlot ?? grenadeOptions[0]?.slot ?? 1);
                const optionLabel = titleCase(option.label ?? option.type ?? option.id ?? `Grenade ${optionSlot}`);

                return (
                  <span
                    key={`${optionSlot}-${option.id ?? optionLabel}`}
                    style={{
                      padding: '4px 8px',
                      border: `1px solid ${selected ? `${grenade.tone}55` : 'rgba(255,255,255,0.08)'}`,
                      background: selected ? `${grenade.tone}12` : 'rgba(255,255,255,0.03)',
                      clipPath: frameClip(),
                      WebkitClipPath: frameClip(),
                      fontSize: 10,
                      letterSpacing: 1.2,
                      textTransform: 'uppercase',
                      color: selected ? '#f4fbff' : 'rgba(231,244,252,0.68)',
                    }}
                  >
                    {optionLabel}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'grid',
            gap: 8,
            padding: compact || minimal ? '8px 9px' : '9px 10px',
            background: pack.ready ? 'rgba(98,221,255,0.08)' : 'rgba(255,255,255,0.03)',
            border: `1px solid ${pack.ready ? 'rgba(98,221,255,0.24)' : 'rgba(255,255,255,0.08)'}`,
            clipPath: frameClip(),
            WebkitClipPath: frameClip(),
            boxShadow: `inset 0 0 0 1px ${pack.tone}10`,
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 10, letterSpacing: 1.8, textTransform: 'uppercase', color: pack.tone }}>{pack.slotLabel}</span>
            <strong style={{ fontSize: compact || minimal ? 13 : 14, letterSpacing: 0.8, textTransform: 'uppercase', color: '#f4fbff' }}>
              {pack.label}
            </strong>
            <span style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'rgba(231,244,252,0.58)' }}>{pack.status}</span>
          </div>
          {minimal ? null : <div style={{ fontSize: 10.5, lineHeight: 1.35, color: 'rgba(231,244,252,0.70)' }}>{pack.detail}</div>}
          {minimal ? null : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {packOptions.slice(0, 4).map((option, index) => {
                const optionSlot = option.slot ?? index + 1;
                const selected = optionSlot === (localPlayer.packSlot ?? packOptions[0]?.slot ?? 1);
                const optionLabel = titleCase(option.label ?? option.type ?? option.id ?? `Pack ${optionSlot}`);

                return (
                  <span
                    key={`${optionSlot}-${option.id ?? optionLabel}`}
                    style={{
                      padding: '4px 8px',
                      border: `1px solid ${selected ? `${pack.tone}55` : 'rgba(255,255,255,0.08)'}`,
                      background: selected ? `${pack.tone}12` : 'rgba(255,255,255,0.03)',
                      clipPath: frameClip(),
                      WebkitClipPath: frameClip(),
                      fontSize: 10,
                      letterSpacing: 1.2,
                      textTransform: 'uppercase',
                      color: selected ? '#f4fbff' : 'rgba(231,244,252,0.68)',
                    }}
                  >
                    {optionLabel}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gap: minimal ? 8 : 10 }}>
          <Meter
            label="Health"
            value={health}
            max={armorProfile.maxHealth}
            tone={resolveHealthBarColor(health, armorProfile.maxHealth)}
            activeLabel={`${Math.round(health)} / ${armorProfile.maxHealth}`}
          />
          <Meter
            label="Energy"
            value={energy}
            max={armorProfile.maxEnergy}
            tone={resolveEnergyBarColor(energy, armorProfile.maxEnergy)}
            activeLabel={`${Math.round(energy)} / ${armorProfile.maxEnergy}`}
          />
          {minimal ? null : (
            <>
              <Meter label={heatLabel} value={heat} tone={overheated ? '#ff7d7d' : weapon.tone} activeLabel={`${Math.round(heat)}%`} />
              <Meter
                label="Cooldown"
                value={cooldown}
                max={weapon.cooldownWindow}
                tone="#86a9ff"
                activeLabel={cooldown > 0.02 ? `${cooldown.toFixed(2)}s` : 'Ready'}
              />
            </>
          )}
        </div>

        {minimal ? null : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <span
              style={{
                padding: '5px 9px',
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.06)',
                clipPath: frameClip(),
                WebkitClipPath: frameClip(),
                fontSize: 10.5,
                letterSpacing: 1.1,
                textTransform: 'uppercase',
                color: '#f4fbff',
              }}
            >
              {weapon.rawId ?? 'unarmed'}
            </span>
            {spawnProtected ? (
              <span
                style={{
                  padding: '5px 9px',
                  border: '1px solid rgba(98,221,255,0.22)',
                  background: 'rgba(98,221,255,0.10)',
                  clipPath: frameClip(),
                  WebkitClipPath: frameClip(),
                  fontSize: 10.5,
                  letterSpacing: 1.1,
                  textTransform: 'uppercase',
                  color: '#f4fbff',
                }}
              >
                Spawn Protected
              </span>
            ) : null}
            {carryingFlag ? (
              <span
                style={{
                  padding: '5px 9px',
                  border: '1px solid rgba(255, 204, 102, 0.22)',
                  background: 'rgba(255, 204, 102, 0.10)',
                  clipPath: frameClip(),
                  WebkitClipPath: frameClip(),
                  fontSize: 10.5,
                  letterSpacing: 1.1,
                  textTransform: 'uppercase',
                  color: '#f4fbff',
                }}
              >
                Flag Carrier
              </span>
            ) : null}
            {overheated ? (
              <span
                style={{
                  padding: '5px 9px',
                  border: '1px solid rgba(255, 125, 125, 0.24)',
                  background: 'rgba(255, 125, 125, 0.12)',
                  clipPath: frameClip(),
                  WebkitClipPath: frameClip(),
                  fontSize: 10.5,
                  letterSpacing: 1.1,
                  textTransform: 'uppercase',
                  color: '#f4fbff',
                }}
              >
                Overheated
              </span>
            ) : null}
          </div>
        )}
      </div>
    </HudFrame>
  );
}
